import { useContext } from 'react';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Theme,
  useNextgenRouter,
  useTranslation,
} from '@nextgen-web-framework/all';
import {
  CartEventsEnum,
  CartConstantsEnum,
  CartEvents,
  updateCart,
  getActiveCart,
  cartApi,
  myListApi,
  updateIsCheckoutDisabled,
  updateMyList,
  ListType,
  includesAny,
} from '@commerce-cart-webcomponents/sdk';
import type {
  Cart,
  CartEntry,
  CartEntryDetails,
  InitialCartListState,
  InitialCartState,
  ListEntry,
  ListResponse,
} from '@commerce-cart-webcomponents/sdk';
import { StorageService } from '@nextgen-web-framework/core';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { AllCustomListContext, type CustomListContextType } from '@commerce-cart-webcomponents/ui';
import { AccountProductType } from '@commerce-cart-webcomponents/sdk/services/models/AccountProductType';
import styles from './UpdateProductButton.module.scss';

export type MouseClickEventTye = React.MouseEvent<HTMLSpanElement, MouseEvent>;
export type KeyPressEventTye = React.KeyboardEvent<HTMLImageElement>;
export type BlurEvent = React.FocusEvent<HTMLInputElement, Element>;
export type MouseClickOrKeyPressEventTye = KeyPressEventTye | MouseClickEventTye | BlurEvent;

interface UpdateProductButtonProps {
  data: {
    productId: string;
    updatedProductId: string;
    entryId?: number;
  };
  disabled?: boolean;
  size?: ButtonSize;
  onProductUpdated?: (res: Cart) => void;
}

export const UpdateProductButton = (props: UpdateProductButtonProps) => {
  const { data, onProductUpdated, size, disabled = false } = props;
  const router = useNextgenRouter();
  const dispatch = useDispatch<AppDispatch>();
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const { myList }: InitialCartListState = useSelector((state: AppState) => state.commerceMyList);
  const listId = router.params?.listId ?? '';
  const [listIdWithoutAccountID] = (listId as String).split('#');
  const { customList, updateCustomList }: CustomListContextType = useContext(AllCustomListContext);
  const currentList = listIdWithoutAccountID === myList?.listId ? myList : customList;
  const isList = listIdWithoutAccountID && listIdWithoutAccountID === currentList?.listId;
  const cartId = router.params?.cartId ?? '';
  const activeCart = getActiveCart(cart, String(cartId));
  const t = useTranslation();

  const isRegistrationProduct = activeCart?.entries?.some((entry: CartEntryDetails) => {
    if (entry.product.id === data.productId) {
      return includesAny(entry, [AccountProductType.Ekit, AccountProductType.Pkit]);
    }
    return false;
  });
  const updateProduct = () => {
    const cartEntry: CartEntry | undefined = activeCart?.entries?.find(
      (entry: CartEntry) => data.entryId == entry.entryId,
    );

    const listEntry: ListEntry | undefined = currentList?.payLoad?.entries?.find(
      (entry: ListEntry) => data.entryId == entry.entryId,
    );
    const updateListItemPayload = {
      entry: {
        entryId: listEntry?.entryId,
        bundleSlots: listEntry?.bundleSlots,
        product: {
          id: data.updatedProductId as string,
        },
        quantity: listEntry?.quantity as number,
      },
      version: currentList?.version as number,
    };

    const payload = {
      entry: {
        product: {
          id: data.updatedProductId,
        },
        quantity: cartEntry?.quantity as number,
        sopElementId: cartEntry?.sopElementId,
      },
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    isList
      ? myListApi
          .changeQtyForEntryFromList({
            entryId: data.entryId,
            listId: currentList?.listId,
            payload: updateListItemPayload,
          })
          .then((updatedList: ListResponse) => {
            if (updatedList.category === ListType.Wishlist) {
              dispatch(updateMyList(updatedList ?? {}));
            }
            updateCustomList(updatedList);
            if (onProductUpdated) {
              onProductUpdated(cart as Cart);
            }
          })
      : cartApi
          .changeQtyForEntryFromCart({
            cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
            subCartId: activeCart?.subCartId,
            entryId: data?.entryId,
            payload,
          })
          .then((res) => {
            dispatch(updateCart(res as Cart));
            CartEvents.publishEvent({
              event: CartEventsEnum.AddToCart,
              payload: {
                cart: res,
                cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
              },
            });
            if (onProductUpdated) {
              onProductUpdated(res as Cart);
            }
          });
  };

  const updateRegProduct = async () => {
    const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
    const res = (await cartApi.updateCart(cartId, cartId, {
      accountProcess: {
        ...cart.accountProcess,
        variantProduct: data.updatedProductId,
      },
      version: +StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion),
    })) as Cart;
    dispatch(updateIsCheckoutDisabled(true));
    dispatch(updateCart(res));
    if (onProductUpdated) {
      onProductUpdated(res as Cart);
    }
  };

  return (
    <div className={styles['update-button-container']}>
      <div className={styles['container']}>
        <Button
          id="update-product-button"
          onClick={isRegistrationProduct ? updateRegProduct : updateProduct}
          buttonWidth={ButtonWidth.FULL_WIDTH}
          title={t('cart.updateButton.updateBtnLabel')}
          disabled={disabled}
          tabIndex={0}
          buttonStyle={ButtonStyles.SOLID}
          theme={Theme.BLACK}
          size={size}
          role="button"
          aria-label={'update-cart-btn'}
          data-testid="update-cart-btn"
        />
      </div>
    </div>
  );
};

export type { UpdateProductButtonProps };
